import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'

import { IAddColumnBody } from '@makyoapp/makyo-sdk/api/Tables'
import { ColumnTypes, ITableColumn, ITableRow } from '@makyoapp/makyo-sdk/models/Table'
import { DEFAULT_DATETIME_COL_OPTIONS, DEFAULT_NUMBER_COL_OPTIONS } from '@makyoapp/makyo-common'
import { Check16, Info16, Refresh16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { DEFAULT_USER_SPECIFIC } from '@/core/db'
import GridImportWorker from '@/core/db/operations/import-worker?worker'
import { workerRequest } from '@/workers/worker-request'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGridImportData } from '@/hooks/useGridImportData'
import { useReqError } from '@/hooks/useReqError'
import { EVENT_KEYS } from '@/shared/custom-event'
import { IMPORT_PROCESSING_STATE, IMPORT_STEPS, ImportItem, ImportProcessingState } from '@/utils/grid-import'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

function getDefaultSettingForColumn(type: ColumnTypes): Record<string, any> {
  switch (type) {
    case ColumnTypes.Number:
      return { ...DEFAULT_NUMBER_COL_OPTIONS }
    case ColumnTypes.DateTime:
      return { ...DEFAULT_DATETIME_COL_OPTIONS }
    default:
      return {}
  }
}

// Helper function to generate unique column name
function getUniqueColumnName(baseName: string, existingColumnNames: string[]) {
  let columnName = baseName
  let counter = 1
  while (existingColumnNames.includes(columnName)) {
    columnName = `${baseName} ${counter}`
    counter++
  }
  existingColumnNames.push(columnName) // Add to existing names to prevent duplicates in current batch
  return columnName
}

/**
 * LoadingSpinner component
 * Renders a simple spinning animation to indicate loading state
 */
const LoadingSpinner = () => (
  <div className='ml-2 h-4 w-4 animate-spin rounded-full border-3 border-solid border-t-transparent mr-2'></div>
)

/**
 * Helper component for processing status
 */
const ProcessingStatus = ({ state }: { state?: ImportProcessingState }) => (
  <span
    className={clsx('flex items-center', {
      'text-teal-500': state === IMPORT_PROCESSING_STATE.COMPLETE,
      'text-red-600 dark:text-red-400': state === IMPORT_PROCESSING_STATE.ERROR,
    })}
  >
    {state === IMPORT_PROCESSING_STATE.COMPLETE && <Check16 width={24} height={24} className='mr-2' />}
    {state === IMPORT_PROCESSING_STATE.PROCESSING && <LoadingSpinner />}
    {state === IMPORT_PROCESSING_STATE.ERROR && (
      <Info16 width={16} height={16} className='text-red-600 dark:text-red-400 mr-2' />
    )}
    <Trans>{state || IMPORT_PROCESSING_STATE.WAITING}</Trans>
  </span>
)

let worker: Worker | null = null

async function importWorkerRequest<T>(payload: { type: string; data: any }): Promise<T> {
  const requestId = uuidv4()
  if (!worker) {
    worker = new GridImportWorker()
  }
  return workerRequest<T>(worker, { ...payload, requestId })
}

/**
 * Grid Import: Process Data step
 *
 * Handles the process of importing data, including:
 * - Preparing data
 * - Creating a new table
 * - Setting up columns
 * - Inserting data
 * Displays progress and status for each step
 */
const GridImportProcessData = () => {
  const [newTableIds, setNewTableIds] = useState<string[]>([])
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(-1)
  const [appendProcessFinished, setAppendProcessFinished] = useState(false)
  const [processingError, setProcessingError] = useState(false)

  const isStartedRef = useRef(false)
  const processStartedRef = useRef(false)

  const navigate = useNavigate()
  const printAPIError = useReqError()

  // Get db client and active table
  const { dbClient, dbId, language } = useDbClientContext()
  const { activeTable } = useGridV2Context()

  // Get import state
  const { importState, prevStep, updateProcessingState, resetImport } = useGridImportData()
  const enabledItems = importState.items.filter((item) => !item.disabled)

  useEffect(() => {
    if (isStartedRef.current === false) {
      handleStartProcessing()
    }
    return () => {
      isStartedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ================================= add new table data =================================
  const processImportData = useCallback(
    async (item: ImportItem) => {
      if (!dbClient || !importState.dbId || processStartedRef.current) return

      processStartedRef.current = true

      const index = item.index

      let tableIdResult: string | null = null

      try {
        // Create table
        updateProcessingState(`createTable_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)
        console.log('create table...')
        const newTable = await dbClient?.CreateTable(item.name, 'import')
        tableIdResult = newTable.id
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_CREATED, { detail: newTable.id }))
        updateProcessingState(`createTable_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)

        // Create columns
        updateProcessingState(`createColumns_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)
        if (!newTable?.settings._id) throw new Error('Table settings ID is undefined')

        const columnHeaders = Object.values(item.columnHeaders ?? {})
        if (Object.keys(columnHeaders).length === 0) return

        const orderedColumnHeaders = columnHeaders
          .filter(
            (column, index, self) =>
              // filter hidden columns
              column.isHidden === false &&
              // filter unique by its name
              self.findIndex((c) => c.title.toLowerCase() === column.title.toLowerCase()) === index
          )
          // sort by original index to maintain the same order as the original data
          .sort((a, b) => a.originalIndex - b.originalIndex)

        console.log(
          'orderedColumnHeaders',
          orderedColumnHeaders.map((c) => c.title)
        )

        // add columns to the table (sequentially)
        const tableColumns: ITableColumn[] = []

        // create columns
        for (const column of orderedColumnHeaders) {
          const newColumn = await newTable.InjectColumn(
            newTable.settings._id,
            {
              _id: '',
              _Type: column.columnType,
              _Name: getUniqueColumnName(
                column.title || column.columnType,
                tableColumns.map((c) => c._Name)
              ),
              _Settings: getDefaultSettingForColumn(column.columnType),
              _Connection: {
                _ForeignColumnIDToShow: '',
                _ForeignColumnID: '',
                _ForeignTableID: '',
                _LocalColumnID: '',
                _MatchMultiple: false,
              },
              _Lookup: {
                _ForeignColumnIDToShow: '',
                _ConnectionColumnID: '',
                _Standard: false,
              },
            } as IAddColumnBody,
            dbId
          )
          tableColumns.push(newColumn)
        }
        console.log(
          'tableColumns',
          tableColumns.map((c) => [c._id, c._Name])
        )

        // create slug column if not exists
        const alreadyHasSlugColumn = tableColumns.some(
          (c) => c._Type === ColumnTypes.Slug || c._Name.toLowerCase().includes('slug')
        )
        if (tableColumns.length > 0 && !alreadyHasSlugColumn) {
          console.log('create slug column...')
          const slugColumn = await newTable.InjectColumn(
            newTable.settings._id,
            {
              _id: '',
              _Name: getUniqueColumnName(
                'slug',
                tableColumns.map((c) => c._Name)
              ),
              _Type: ColumnTypes.Slug,
              _Settings: {
                slugColumn: tableColumns[0]._id,
              },
              _Connection: {
                _LocalColumnID: '',
                _ForeignTableID: '',
                _ForeignColumnID: '',
                _ForeignColumnIDToShow: '',
                _MatchMultiple: false,
              },
              _Lookup: {
                _ForeignColumnIDToShow: '',
                _ConnectionColumnID: '',
                _Standard: false,
              },
            },
            dbId
          )
          tableColumns.push(slugColumn)
        }

        updateProcessingState(`createColumns_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)

        // Update dbWorker with new table
        dbClient.dbWorker.sendAction({
          type: 'set_tables_infos',
          payload: [newTable.settings],
        })

        // Prepare data
        updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)

        const tableRows = await importWorkerRequest<ITableRow[]>({
          type: 'build-import-data',
          data: {
            item,
            columnIndexes: orderedColumnHeaders.map((c) => c.originalIndex),
            columnsData: tableColumns.map((col) => ({ id: col._id, type: col._Type })),
          },
        })
        console.log('tableRows', tableRows)

        updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)

        // Insert data
        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)

        await newTable.AppendRows(tableRows, language, DEFAULT_USER_SPECIFIC)

        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)

        dbClient.notifySubscribers()
        newTable.notifySubscribers()

        return newTable
        //
      } catch (error) {
        updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.ERROR)
        updateProcessingState(`createTable_${index}`, IMPORT_PROCESSING_STATE.ERROR)
        updateProcessingState(`createColumns_${index}`, IMPORT_PROCESSING_STATE.ERROR)
        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.ERROR)

        // remove new table
        if (tableIdResult) {
          await dbClient.DeleteTable(tableIdResult)
        }
        throw error
      } finally {
        processStartedRef.current = false
      }
    },
    [dbClient, dbId, importState.dbId, language, updateProcessingState]
  )

  const handleStartImport = useCallback(async () => {
    setProcessingError(false)
    try {
      const tableIds: string[] = []
      const newTables: Record<string, any> = {}

      // Initialize all tables with "Waiting" status
      for (const item of enabledItems) {
        updateProcessingState(`prepareData_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
        updateProcessingState(`createTable_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
        updateProcessingState(`createColumns_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
        updateProcessingState(`insertData_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
      }

      // Process tables sequentially
      for (let i = 0; i < (enabledItems.length || 0); i++) {
        setCurrentProcessingIndex(i)
        const newTable = await processImportData(enabledItems[i])
        if (newTable?.settings._id) {
          tableIds.push(newTable.settings._id)
          newTables[newTable.settings._id] = newTable
        }
      }

      setCurrentProcessingIndex(-1)

      setNewTableIds(tableIds)
      //
    } catch (err) {
      console.error(err)
      printAPIError(err as any)
      setProcessingError(true)
    }
  }, [enabledItems, printAPIError, processImportData, updateProcessingState])

  // ================================= append table data =================================

  const processAppendData = useCallback(
    async (item: ImportItem) => {
      if (!dbClient || !activeTable || !importState.dbId) return

      const index = item.index

      // Prepare data
      updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)

      const tableRows = await importWorkerRequest<ITableRow[]>({
        type: 'build-append-data',
        data: { item, columnsData: activeTable.settings._Columns.map((col) => ({ id: col._id, type: col._Type })) },
      })

      if (!tableRows.length) {
        updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.ERROR)
        return
      }

      updateProcessingState(`prepareData_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)

      // Insert data
      try {
        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.PROCESSING)
        await activeTable.AppendRows(tableRows, language, DEFAULT_USER_SPECIFIC)
        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.COMPLETE)
      } catch (err) {
        updateProcessingState(`insertData_${index}`, IMPORT_PROCESSING_STATE.ERROR)
        throw err
      }
    },
    [dbClient, activeTable, importState.dbId, updateProcessingState, language]
  )

  const handleStartAppend = useCallback(async () => {
    setProcessingError(false)
    try {
      console.log('start append process...')

      // Initialize all tables with "Waiting" status
      for (const item of enabledItems) {
        updateProcessingState(`prepareData_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
        updateProcessingState(`insertData_${item.index}`, IMPORT_PROCESSING_STATE.WAITING)
      }

      // Process tables sequentially
      for (let i = 0; i < (enabledItems.length || 0); i++) {
        setCurrentProcessingIndex(i)
        await processAppendData(enabledItems[i])
      }
      setAppendProcessFinished(true)
      setCurrentProcessingIndex(-1)
      //
    } catch (err) {
      console.error(err)
      printAPIError(err as any)
      setProcessingError(true)
    }
  }, [enabledItems, printAPIError, processAppendData, updateProcessingState])

  // ================================= start processing =================================
  const handleStartProcessing = useCallback(() => {
    if (importState.type === 'append') {
      handleStartAppend()
    } else {
      handleStartImport()
    }
  }, [handleStartAppend, handleStartImport, importState.type])

  // ================================= on finish processing =================================

  const cleanUp = () => {
    resetImport()

    if (worker) {
      worker.terminate()
      worker = null
    }
  }

  const onFinishImport = () => {
    if (newTableIds.length > 0) {
      const currentPath = window.location.pathname
      const newPath = currentPath.split('/').slice(0, -1).join('/') + '/' + newTableIds[0]
      navigate(newPath)
      cleanUp()
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_CLIENT_RELOAD))
    }
  }

  const onFinishAppend = () => {
    cleanUp()
  }

  return (
    <>
      <Modal.Body className='p-0 max-h-[70vh] overflow-scroll'>
        <div className='container w-[768px] max-w-full p-4 rounded-lg'>
          <div className='space-y-2 scrollable-container'>
            {/* Show all tables with their respective statuses */}
            {enabledItems.map((item) => {
              const isProcessing = currentProcessingIndex === item.index
              return (
                <div key={item.index} className='mb-4'>
                  <div className='flex items-center gap-2'>
                    {isProcessing && (
                      <>
                        {processingError ? (
                          <Info16 width={16} height={16} className='text-red-600 dark:text-red-400' />
                        ) : (
                          <Refresh16 width={16} height={16} className='animate-spin' />
                        )}
                      </>
                    )}
                    <div className='font-semibold'>
                      {importState.type === 'import' ? item.name : activeTable?.settings._Name}
                    </div>
                  </div>

                  <div className='mt-2 space-y-2'>
                    {/* Create Table Status */}
                    {importState.type === 'import' && (
                      <div className='flex justify-between items-center text-sm'>
                        <span>
                          <Trans>Table Creation</Trans>
                        </span>
                        <ProcessingStatus state={importState.state[`createTable_${item.index}`]} />
                      </div>
                    )}

                    {/* Set Up Columns Status */}
                    {importState.type === 'import' && (
                      <div className='flex justify-between items-center text-sm'>
                        <span>
                          <Trans>Column Setup</Trans>
                        </span>
                        <ProcessingStatus state={importState.state[`createColumns_${item.index}`]} />
                      </div>
                    )}

                    {/* Prepare Data Status - Common for all */}
                    <div className='flex justify-between items-center text-sm'>
                      <span>
                        <Trans>Data Preparation</Trans>
                      </span>
                      <ProcessingStatus state={importState.state[`prepareData_${item.index}`]} />
                    </div>

                    {/* Insert Data Status */}
                    <div className='flex justify-between items-center text-sm'>
                      <span>
                        <Trans>Data Insertion</Trans>
                      </span>
                      <ProcessingStatus state={importState.state[`insertData_${item.index}`]} />
                    </div>
                  </div>

                  {item.index < (importState.items.length || 0) - 1 && (
                    <hr className='my-4  border-zinc-200 dark:border-slate-600' />
                  )}
                  {/* Add line between tables */}
                </div>
              )
            })}
          </div>
        </div>
      </Modal.Body>

      {importState.step !== IMPORT_STEPS.SELECT_FILE && (
        <Modal.Footer>
          <div className='flex justify-between w-full'>
            {!newTableIds.length && (
              <Button onClick={prevStep} variant={ButtonVariant.Outlined}>
                <Trans>Cancel</Trans>
              </Button>
            )}

            {importState.type === 'import' && newTableIds.length > 0 && (
              <div className='flex justify-end w-full'>
                <Button onClick={onFinishImport}>
                  <Trans>Open Table</Trans>
                </Button>
              </div>
            )}

            {importState.type === 'append' && appendProcessFinished && (
              <Button onClick={onFinishAppend}>
                <Trans>Finish</Trans>
              </Button>
            )}
          </div>
        </Modal.Footer>
      )}
    </>
  )
}

export default GridImportProcessData
